/**
 * Acquisition Due Diligence — Assessment items for 7 DD categories.
 *
 * Follows the same green/yellow/red + photo + notes pattern as daily
 * self-assessment (DailyAssessmentItem shape). Each item evaluates one
 * aspect of the target facility across the acquisition DD framework.
 *
 * Rating scale (same as daily assessment):
 *   1 = Green  — Low risk, meets expectations
 *   2 = Yellow — Moderate risk, needs further investigation
 *   3 = Red    — High risk, material concern for transaction
 *
 * Sources: ASC_EVALUATOR_BRAIN.md, ASC_INDEX.md (128 KB files)
 */

import { invokeFunction } from '../api'
import { toApiRequestError } from '../errors'
import type { AssessmentRating } from './dailyAssessment'

// ── Acquisition DD Categories ──────────────────────────────────────────────

export const ACQUISITION_CATEGORIES = [
  'FINANCIAL',
  'EQUIPMENT',
  'STAFFING',
  'MARKET',
  'FACILITY',
  'REGULATORY',
  'IT',
] as const

export type AcquisitionCategory = (typeof ACQUISITION_CATEGORIES)[number]

export const CATEGORY_LABELS: Record<AcquisitionCategory, string> = {
  FINANCIAL: 'Financial',
  EQUIPMENT: 'Capital Equipment',
  STAFFING: 'Staffing',
  MARKET: 'Market',
  FACILITY: 'Facility',
  REGULATORY: 'Regulatory Risk',
  IT: 'IT Systems',
}

export const CATEGORY_DESCRIPTIONS: Record<AcquisitionCategory, string> = {
  FINANCIAL: 'Revenue, profitability, payer mix, and accounts receivable health.',
  EQUIPMENT: 'Medical equipment inventory, age, condition, and replacement needs.',
  STAFFING: 'Staff ratios, turnover, credentials, and key person risk.',
  MARKET: 'Service area demographics, competition, and physician alignment.',
  FACILITY: 'Lease terms, building condition, expansion capacity, and compliance.',
  REGULATORY: 'Survey history, open CAPAs, complaint history, and enforcement actions.',
  IT: 'EHR, practice management, cybersecurity, and integration capability.',
}

// ── Item Type ──────────────────────────────────────────────────────────────

export interface AcquisitionAssessmentItem {
  item_id: string
  category: AcquisitionCategory
  label: string
  description: string
  standard_ref: string
  evidence_type: ('photo' | 'document' | 'interview' | 'observation')[]
}

export interface AcquisitionAssessmentResponse {
  item_id: string
  rating: AssessmentRating
  photo_id: string | null
  document_ids: string[]
  note: string
  assessed_at: string
}

export interface AcquisitionCategorySummary {
  category: AcquisitionCategory
  item_count: number
  assessed_count: number
  average_rating: number
  risk_level: 'low' | 'moderate' | 'high'
  red_count: number
  yellow_count: number
  green_count: number
}

export type AcquisitionRecommendation = 'go' | 'conditional' | 'no_go'

// ── Assessment Items by Category ───────────────────────────────────────────

const FINANCIAL_ITEMS: Omit<AcquisitionAssessmentItem, 'item_id'>[] = [
  {
    category: 'FINANCIAL',
    label: 'EBITDA trend — 3-year trailing',
    description: 'Review financial statements for EBITDA trajectory. Green: stable/growing. Yellow: flat or single-year dip. Red: declining trend.',
    standard_ref: 'DD-FIN',
    evidence_type: ['document'],
  },
  {
    category: 'FINANCIAL',
    label: 'Revenue per case by specialty',
    description: 'Evaluate case-level revenue by surgical specialty. Compare to regional benchmarks. Identify under-performing specialties.',
    standard_ref: 'DD-FIN',
    evidence_type: ['document'],
  },
  {
    category: 'FINANCIAL',
    label: 'Payer mix — Medicare/Medicaid/Commercial/Self-pay',
    description: 'Assess payer distribution. Green: balanced with strong commercial. Yellow: heavy Medicare/Medicaid. Red: >60% government payers or high self-pay.',
    standard_ref: 'DD-FIN',
    evidence_type: ['document'],
  },
  {
    category: 'FINANCIAL',
    label: 'Accounts receivable aging',
    description: 'Review AR aging buckets. Green: <5% over 120 days. Yellow: 5-15% over 120 days. Red: >15% over 120 days or rising trend.',
    standard_ref: 'DD-FIN',
    evidence_type: ['document'],
  },
  {
    category: 'FINANCIAL',
    label: 'Physician ownership/investment structure',
    description: 'Document ownership percentages, physician investors, and any non-compete or lock-up provisions. Assess key surgeon concentration risk.',
    standard_ref: 'DD-FIN',
    evidence_type: ['document', 'interview'],
  },
  {
    category: 'FINANCIAL',
    label: 'Out-of-network revenue exposure',
    description: 'Quantify OON revenue as percentage of total. Green: <10%. Yellow: 10-25%. Red: >25% (regulatory/market risk of surprise billing rules).',
    standard_ref: 'DD-FIN',
    evidence_type: ['document'],
  },
  {
    category: 'FINANCIAL',
    label: 'Managed care contracts — terms and expirations',
    description: 'List top 5 payer contracts with expiration dates and rate escalation terms. Flag contracts expiring within 12 months of close.',
    standard_ref: 'DD-FIN',
    evidence_type: ['document'],
  },
]

const EQUIPMENT_ITEMS: Omit<AcquisitionAssessmentItem, 'item_id'>[] = [
  {
    category: 'EQUIPMENT',
    label: 'Equipment inventory — completeness and accuracy',
    description: 'Verify a complete asset list exists with serial numbers, acquisition dates, and original cost. Green: current and complete. Red: no formal inventory.',
    standard_ref: '§416.44(a) ENV',
    evidence_type: ['document', 'photo'],
  },
  {
    category: 'EQUIPMENT',
    label: 'Average equipment age by category',
    description: 'Calculate average age of major equipment classes (imaging, surgical, monitoring, sterilizers). Green: <7 years avg. Yellow: 7-12 years. Red: >12 years.',
    standard_ref: '§416.44(a) ENV',
    evidence_type: ['document'],
  },
  {
    category: 'EQUIPMENT',
    label: 'End-of-life equipment — replacement cost estimate',
    description: 'Identify equipment past useful life or within 2 years of EOL. Estimate total replacement capital required. Flag unfunded replacement needs.',
    standard_ref: 'DD-EQUIP',
    evidence_type: ['document', 'photo'],
  },
  {
    category: 'EQUIPMENT',
    label: 'Maintenance contracts and service history',
    description: 'Review preventive maintenance (PM) records and service contracts. Green: all major equipment under PM/service contract. Red: lapsed contracts or missing PM logs.',
    standard_ref: '§416.44(a) ENV',
    evidence_type: ['document'],
  },
  {
    category: 'EQUIPMENT',
    label: 'Sterilizer condition — type, age, BI/CI logs',
    description: 'Inspect sterilization equipment. Verify BI testing frequency (daily or per-implant load), CI in every package, and current maintenance. Photo the sterilizer log.',
    standard_ref: '§416.51(b) SPD',
    evidence_type: ['photo', 'document'],
  },
  {
    category: 'EQUIPMENT',
    label: 'Imaging equipment — modality and currency',
    description: 'List all imaging equipment (C-arm, ultrasound, X-ray). Verify current calibration and accreditation where required. Assess technology generation.',
    standard_ref: 'DD-EQUIP',
    evidence_type: ['photo', 'document'],
  },
  {
    category: 'EQUIPMENT',
    label: 'Equipment recalls — tracked and resolved',
    description: 'Verify a process exists for tracking FDA recalls. Check that all active recalls have been addressed. Green: documented recall program. Red: no tracking process.',
    standard_ref: '§416.44(a) ENV',
    evidence_type: ['document'],
  },
]

const STAFFING_ITEMS: Omit<AcquisitionAssessmentItem, 'item_id'>[] = [
  {
    category: 'STAFFING',
    label: 'Clinical staffing ratios by department',
    description: 'Review nurse-to-patient ratios in Pre-Op, OR, and PACU. Compare to state requirements and AORN staffing guidelines. Green: meets/exceeds. Red: below minimums.',
    standard_ref: '§416.45 HR',
    evidence_type: ['document', 'interview'],
  },
  {
    category: 'STAFFING',
    label: 'Staff turnover rate — 12-month trailing',
    description: 'Calculate annualized turnover by role (RN, surgical tech, admin). Green: <15%. Yellow: 15-30%. Red: >30% or losing critical roles.',
    standard_ref: 'DD-STAFF',
    evidence_type: ['document'],
  },
  {
    category: 'STAFFING',
    label: 'Key person risk assessment',
    description: 'Identify single-point-of-failure roles (administrator, IC coordinator, lead surgical tech, billing manager). Flag any positions held by one person with no backup.',
    standard_ref: 'DD-STAFF',
    evidence_type: ['interview'],
  },
  {
    category: 'STAFFING',
    label: 'Credential currency — all clinical staff',
    description: 'Spot-check 5-10 clinical staff files for current licenses, BLS/ACLS, competency assessments, and annual training completion. Red: any expired credentials in active files.',
    standard_ref: '§416.45 HR',
    evidence_type: ['document'],
  },
  {
    category: 'STAFFING',
    label: 'Compensation benchmarking vs. market',
    description: 'Compare salary/benefit levels to regional market data. Green: competitive. Yellow: below market but stable. Red: significantly below market with retention issues.',
    standard_ref: 'DD-STAFF',
    evidence_type: ['document'],
  },
  {
    category: 'STAFFING',
    label: 'Pending HR issues — complaints, EEOC, litigation',
    description: 'Request disclosure of any pending HR complaints, EEOC charges, wage/hour claims, or employment litigation. Green: none. Red: active claims or pattern.',
    standard_ref: 'DD-STAFF',
    evidence_type: ['document', 'interview'],
  },
  {
    category: 'STAFFING',
    label: 'Anesthesia provider model and coverage',
    description: 'Document anesthesia staffing model (employed vs. contracted, MD vs. CRNA, supervision model). Verify state opt-out compliance. Assess coverage adequacy for case volume.',
    standard_ref: '§416.42 ANES',
    evidence_type: ['document', 'interview'],
  },
]

const MARKET_ITEMS: Omit<AcquisitionAssessmentItem, 'item_id'>[] = [
  {
    category: 'MARKET',
    label: 'Service area demographics',
    description: 'Analyze population, age distribution, and insurance coverage within 15-mile radius. Green: growing, well-insured. Red: declining population or high uninsured rate.',
    standard_ref: 'DD-MKT',
    evidence_type: ['document'],
  },
  {
    category: 'MARKET',
    label: 'Competitor landscape — ASCs and HOPDs',
    description: 'Map competing ASCs and hospital outpatient departments within 15 miles. Assess overlap in specialties and capacity. Green: limited competition. Red: oversaturated market.',
    standard_ref: 'DD-MKT',
    evidence_type: ['document'],
  },
  {
    category: 'MARKET',
    label: 'Specialty mix and optimization opportunity',
    description: 'Review current specialty mix (ophthalmology, ortho, GI, pain, ENT, etc.). Identify underserved high-value specialties that could be added with existing infrastructure.',
    standard_ref: 'DD-MKT',
    evidence_type: ['document', 'interview'],
  },
  {
    category: 'MARKET',
    label: 'Physician referral patterns and alignment',
    description: 'Identify top 10 referring physicians and their case volumes. Assess physician loyalty (exclusive vs. multi-facility). Flag any physicians accounting for >25% of volume.',
    standard_ref: 'DD-MKT',
    evidence_type: ['document', 'interview'],
  },
  {
    category: 'MARKET',
    label: 'Physician pipeline and recruitment',
    description: 'Assess physician recruitment pipeline. Green: active recruitment with near-term prospects. Yellow: stable but no growth pipeline. Red: losing surgeons with no replacements.',
    standard_ref: 'DD-MKT',
    evidence_type: ['interview'],
  },
  {
    category: 'MARKET',
    label: 'Case volume trends — 3-year trailing',
    description: 'Review total case volume by year and by specialty. Green: stable/growing. Yellow: flat. Red: declining >5% annually.',
    standard_ref: 'DD-MKT',
    evidence_type: ['document'],
  },
  {
    category: 'MARKET',
    label: 'Site-of-service shift opportunity',
    description: 'Identify HOPD procedures that could migrate to ASC setting under current CMS payment rules. Quantify incremental case volume potential.',
    standard_ref: 'DD-MKT',
    evidence_type: ['document'],
  },
]

const FACILITY_ITEMS: Omit<AcquisitionAssessmentItem, 'item_id'>[] = [
  {
    category: 'FACILITY',
    label: 'Lease terms — expiration, renewal, TI allowance',
    description: 'Review lease agreement for remaining term, renewal options, escalation clauses, and tenant improvement provisions. Red: <3 years remaining with no renewal.',
    standard_ref: 'DD-FAC',
    evidence_type: ['document'],
  },
  {
    category: 'FACILITY',
    label: 'Building condition — roof, HVAC, plumbing, electrical',
    description: 'Walk the facility exterior and mechanical spaces. Photo any deferred maintenance, water damage, or structural concerns. Request last building inspection report.',
    standard_ref: '§416.44(a) ENV',
    evidence_type: ['photo', 'document', 'observation'],
  },
  {
    category: 'FACILITY',
    label: 'HVAC system — OR ventilation compliance',
    description: 'Verify OR meets ASHRAE 170 requirements: 20 ACH, 4 outside air, positive pressure, temp 68-75F, humidity 20-60%. Photo temp/humidity logs and air handling unit.',
    standard_ref: 'ASHRAE 170-2017',
    evidence_type: ['photo', 'document', 'observation'],
  },
  {
    category: 'FACILITY',
    label: 'Expansion capacity — additional ORs/procedure rooms',
    description: 'Assess whether the facility can accommodate additional ORs, procedure rooms, or recovery bays within the existing footprint or adjacent space.',
    standard_ref: 'DD-FAC',
    evidence_type: ['photo', 'observation'],
  },
  {
    category: 'FACILITY',
    label: 'ADA accessibility compliance',
    description: 'Walk entrance, hallways, restrooms, and patient areas for ADA compliance. Photo any barriers. Green: fully compliant. Red: significant access barriers.',
    standard_ref: 'ADA',
    evidence_type: ['photo', 'observation'],
  },
  {
    category: 'FACILITY',
    label: 'Zoning and permitting status',
    description: 'Verify current zoning allows ASC use. Check for any pending code enforcement actions. Request certificate of occupancy and any conditional use permits.',
    standard_ref: 'DD-FAC',
    evidence_type: ['document'],
  },
  {
    category: 'FACILITY',
    label: 'Environmental assessment — Phase I/II if warranted',
    description: 'For owned properties or pre-1990 buildings: determine if Phase I ESA has been conducted. Flag asbestos, lead paint, or underground storage tank concerns.',
    standard_ref: 'DD-FAC',
    evidence_type: ['document'],
  },
  {
    category: 'FACILITY',
    label: 'Parking adequacy',
    description: 'Count available parking spaces vs. peak daily patient/staff volume. Green: adequate with margin. Yellow: tight but functional. Red: insufficient, patient complaints.',
    standard_ref: 'DD-FAC',
    evidence_type: ['photo', 'observation'],
  },
]

const REGULATORY_ITEMS: Omit<AcquisitionAssessmentItem, 'item_id'>[] = [
  {
    category: 'REGULATORY',
    label: 'CMS survey history — last 3 years',
    description: 'Obtain CMS Form 2567 statements for last 3 survey cycles. Count deficiencies by domain. Green: minimal findings. Red: repeat deficiencies or condition-level citations.',
    standard_ref: '42 CFR 416',
    evidence_type: ['document'],
  },
  {
    category: 'REGULATORY',
    label: 'Accreditation survey results — last cycle',
    description: 'Review most recent accreditation survey report (TJC/AAAHC/ACHC/QUAD A). Note any Requirements for Improvement (RFIs) or corrective action requirements.',
    standard_ref: 'DD-REG',
    evidence_type: ['document'],
  },
  {
    category: 'REGULATORY',
    label: 'Open CAPAs — count, age, domains',
    description: 'List all open corrective action plans. Assess by domain, age, and completion status. Green: 0-2 open, all <90 days. Red: >5 open or any >180 days.',
    standard_ref: '§416.43 QAPI',
    evidence_type: ['document'],
  },
  {
    category: 'REGULATORY',
    label: 'Immediate Jeopardy history — ever',
    description: 'Determine if the facility has ever received an IJ citation. If yes: what tag, what corrective action, and was it cleared. Any IJ in last 3 years is a red flag.',
    standard_ref: 'CMS Appendix Q',
    evidence_type: ['document'],
  },
  {
    category: 'REGULATORY',
    label: 'Complaint investigation history',
    description: 'Request state survey agency complaint investigation results for last 3 years. Green: none or all unsubstantiated. Red: substantiated complaints, especially IC or patient harm.',
    standard_ref: 'DD-REG',
    evidence_type: ['document'],
  },
  {
    category: 'REGULATORY',
    label: 'State licensure status and conditions',
    description: 'Verify current state license. Check for any conditions, restrictions, or probationary status. Green: unrestricted active license. Red: any restrictions or pending actions.',
    standard_ref: 'DD-REG',
    evidence_type: ['document'],
  },
  {
    category: 'REGULATORY',
    label: 'Pending litigation — patient care related',
    description: 'Request disclosure of all pending malpractice or patient care litigation. Assess volume, severity, and insurance coverage adequacy (occurrence vs. claims-made, tail coverage).',
    standard_ref: 'DD-REG',
    evidence_type: ['document', 'interview'],
  },
  {
    category: 'REGULATORY',
    label: 'OIG/DOJ investigation status',
    description: 'Request disclosure of any current or recent OIG/DOJ investigations, subpoenas, or Corporate Integrity Agreements. Any active investigation is a major red flag.',
    standard_ref: 'DD-REG',
    evidence_type: ['document', 'interview'],
  },
]

const IT_ITEMS: Omit<AcquisitionAssessmentItem, 'item_id'>[] = [
  {
    category: 'IT',
    label: 'EHR system — vendor, version, contract terms',
    description: 'Identify EHR vendor and version. Review contract expiration and data migration provisions. Assess Meaningful Use/MIPS compliance. Green: modern, supported. Red: legacy or end-of-life.',
    standard_ref: 'DD-IT',
    evidence_type: ['document', 'observation'],
  },
  {
    category: 'IT',
    label: 'Practice management / scheduling system',
    description: 'Evaluate PM/scheduling software for adequacy, integration with EHR, and reporting capability. Assess block schedule management and utilization analytics.',
    standard_ref: 'DD-IT',
    evidence_type: ['document', 'observation'],
  },
  {
    category: 'IT',
    label: 'Revenue cycle management system',
    description: 'Review RCM platform (in-house vs. outsourced). Assess claim denial rates, days in AR, and automation level. Green: <5% denial rate. Red: >10% or manual-heavy process.',
    standard_ref: 'DD-IT',
    evidence_type: ['document'],
  },
  {
    category: 'IT',
    label: 'Cybersecurity posture — HIPAA Security Rule',
    description: 'Request last risk assessment, penetration test results, and security incident history. Verify encryption at rest and in transit, access controls, and audit logging.',
    standard_ref: 'HIPAA §164.308',
    evidence_type: ['document', 'interview'],
  },
  {
    category: 'IT',
    label: 'Business continuity / disaster recovery plan',
    description: 'Review BC/DR plan including RTO/RPO targets, backup frequency, off-site storage, and last successful recovery test. Green: tested within 12 months. Red: no plan or untested.',
    standard_ref: 'DD-IT',
    evidence_type: ['document'],
  },
  {
    category: 'IT',
    label: 'Network infrastructure — age and capacity',
    description: 'Assess network hardware (switches, Wi-Fi APs, firewalls) age and bandwidth adequacy. Green: <5 years, adequate bandwidth. Red: >10 years or bottlenecks reported.',
    standard_ref: 'DD-IT',
    evidence_type: ['document', 'observation'],
  },
  {
    category: 'IT',
    label: 'Interoperability — HL7/FHIR capability',
    description: 'Determine if systems support HL7 v2 or FHIR APIs for data exchange. Assess integration readiness with acquirer systems. Green: FHIR-ready. Yellow: HL7 v2 only. Red: no standard interfaces.',
    standard_ref: 'DD-IT',
    evidence_type: ['document', 'interview'],
  },
]

// ── All Items ──────────────────────────────────────────────────────────────

const ALL_ITEMS_RAW = [
  ...FINANCIAL_ITEMS,
  ...EQUIPMENT_ITEMS,
  ...STAFFING_ITEMS,
  ...MARKET_ITEMS,
  ...FACILITY_ITEMS,
  ...REGULATORY_ITEMS,
  ...IT_ITEMS,
]

export const ACQUISITION_ITEMS: AcquisitionAssessmentItem[] = ALL_ITEMS_RAW.map(
  (item, index) => ({
    ...item,
    item_id: `acq-${item.category.toLowerCase()}-${String(index + 1).padStart(3, '0')}`,
  }),
)

// ── Helpers ────────────────────────────────────────────────────────────────

/**
 * Get all items for a specific category.
 */
export function getItemsByCategory(
  category: AcquisitionCategory,
): AcquisitionAssessmentItem[] {
  return ACQUISITION_ITEMS.filter((item) => item.category === category)
}

/**
 * Get the total item count.
 */
export function getTotalItemCount(): number {
  return ACQUISITION_ITEMS.length
}

/**
 * Calculate per-category summary from responses.
 */
export function calculateCategorySummary(
  category: AcquisitionCategory,
  responses: AcquisitionAssessmentResponse[],
): AcquisitionCategorySummary {
  const items = getItemsByCategory(category)
  const responseMap = new Map(responses.map((r) => [r.item_id, r]))
  const categoryResponses = items
    .map((item) => responseMap.get(item.item_id))
    .filter((r): r is AcquisitionAssessmentResponse => r !== undefined)

  const green_count = categoryResponses.filter((r) => r.rating === 1).length
  const yellow_count = categoryResponses.filter((r) => r.rating === 2).length
  const red_count = categoryResponses.filter((r) => r.rating === 3).length
  const assessed_count = categoryResponses.length

  const average_rating =
    assessed_count > 0
      ? Math.round(
          (categoryResponses.reduce((sum, r) => sum + r.rating, 0) /
            assessed_count) *
            100,
        ) / 100
      : 0

  const risk_level: 'low' | 'moderate' | 'high' =
    red_count > 0 || average_rating >= 2.5
      ? 'high'
      : yellow_count > Math.floor(items.length / 2) || average_rating >= 1.8
        ? 'moderate'
        : 'low'

  return {
    category,
    item_count: items.length,
    assessed_count,
    average_rating,
    risk_level,
    red_count,
    yellow_count,
    green_count,
  }
}

/**
 * Calculate overall acquisition recommendation from all category summaries.
 */
export function calculateOverallRecommendation(
  summaries: AcquisitionCategorySummary[],
): AcquisitionRecommendation {
  const highRiskCount = summaries.filter((s) => s.risk_level === 'high').length
  const totalRedItems = summaries.reduce((sum, s) => sum + s.red_count, 0)

  // Any category with high risk + red items in 2+ categories → no_go
  if (highRiskCount >= 2 && totalRedItems >= 3) return 'no_go'
  // Any high risk category → conditional
  if (highRiskCount >= 1) return 'conditional'
  // Some moderate risk → conditional
  const moderateCount = summaries.filter((s) => s.risk_level === 'moderate').length
  if (moderateCount >= 3) return 'conditional'
  // All low or mostly low → go
  return 'go'
}

/**
 * Recommendation label and color.
 */
export const RECOMMENDATION_META: Record<
  AcquisitionRecommendation,
  { label: string; color: string; description: string }
> = {
  go: {
    label: 'Go',
    color: 'text-emerald-700 bg-emerald-100 border-emerald-300',
    description: 'Low risk across all categories. Proceed with standard due diligence closing.',
  },
  conditional: {
    label: 'Conditional',
    color: 'text-amber-700 bg-amber-100 border-amber-300',
    description: 'Moderate risk in one or more categories. Proceed with specific conditions and remediation requirements.',
  },
  no_go: {
    label: 'No-Go',
    color: 'text-rose-700 bg-rose-100 border-rose-300',
    description: 'High risk in multiple categories. Transaction not recommended without significant risk mitigation.',
  },
}

// ── API Functions ──────────────────────────────────────────────────────────

const EDGE_FN = 'acquisition-assessments'

/**
 * Save a single response for a visit.
 */
export async function saveAcquisitionResponse(
  visitId: string,
  payload: { item_id: string; category: AcquisitionCategory; rating: AssessmentRating; note?: string },
): Promise<void> {
  const res = await invokeFunction<{ saved: boolean }>(EDGE_FN, {
    method: 'POST',
    path: `/visits/${visitId}/responses:upsert`,
    body: payload,
  })
  if (res.error) throw toApiRequestError(res.error, 'Acquisition assessment request failed')
}

/**
 * Save multiple responses at once.
 */
export async function saveAcquisitionResponsesBatch(
  visitId: string,
  responses: Array<{ item_id: string; category: AcquisitionCategory; rating: AssessmentRating; note?: string }>,
): Promise<number> {
  const res = await invokeFunction<{ saved: number }>(EDGE_FN, {
    method: 'POST',
    path: `/visits/${visitId}/responses:batch`,
    body: { responses },
  })
  if (res.error) throw toApiRequestError(res.error, 'Acquisition assessment request failed')
  return res.data?.saved ?? 0
}

/**
 * Load all responses for a visit.
 */
export async function loadAcquisitionResponses(
  visitId: string,
): Promise<AcquisitionAssessmentResponse[]> {
  const res = await invokeFunction<{ responses: AcquisitionAssessmentResponse[] }>(EDGE_FN, {
    method: 'GET',
    path: `/visits/${visitId}/responses`,
  })
  if (res.error) throw toApiRequestError(res.error, 'Acquisition assessment request failed')
  return res.data?.responses ?? []
}

/**
 * Load per-category summary and overall recommendation for a visit.
 */
export async function loadAcquisitionSummary(
  visitId: string,
): Promise<{
  summary: AcquisitionCategorySummary[]
  recommendation: AcquisitionRecommendation
}> {
  const res = await invokeFunction<{
    summary: AcquisitionCategorySummary[]
    recommendation: AcquisitionRecommendation
  }>(EDGE_FN, {
    method: 'GET',
    path: `/visits/${visitId}/summary`,
  })
  if (res.error) throw toApiRequestError(res.error, 'Acquisition assessment request failed')
  return {
    summary: res.data?.summary ?? [],
    recommendation: res.data?.recommendation ?? 'go',
  }
}
